/**
 * LayerDrawerContext
 *
 * React context and hook for accessing Layer Drawer state.
 * Separated from Provider for Vite HMR compatibility.
 */

import { createContext, useContext } from 'react';
import mapboxgl from 'mapbox-gl';
import { LayerRegistry, BaseLayer } from '../layers2';
import { LayerConfig } from '../layers2/BaseLayer';
import { Layer } from '../../types/layer';
import { LayerPreset } from '../../types/layerPreset';
import { DrawerHeight } from './utils/layerDrawerUtils';

/**
 * Live Layer definition for layers managed by React components (not database)
 */
export interface LiveLayerDef {
  id: string;
  name: string;
  shortName: string;
  icon: string;
  description?: string;
  enabled: boolean;
}

/**
 * LayerDrawerContext Type
 */
export interface LayerDrawerContextType {
  map: mapboxgl.Map | null;
  registry: LayerRegistry | null;
  layers: Layer[];
  loading: boolean;
  error: string | null;
  drawerHeight: DrawerHeight;
  activeTab: 'active' | 'browse' | 'search';
  searchTerm: string;

  // Live layers (React-managed, not from database)
  liveLayers: LiveLayerDef[];
  registerLiveLayer: (layer: LiveLayerDef, onToggle: (enabled: boolean) => void) => void;
  unregisterLiveLayer: (layerId: string) => void;
  toggleLiveLayer: (layerId: string) => void;
  updateLiveLayerState: (layerId: string, enabled: boolean) => void;

  // Actions
  setMap: (map: mapboxgl.Map) => void;
  refreshLayers: () => Promise<void>;
  toggleLayer: (layerId: string) => Promise<void>;
  setLayerOpacity: (layerId: string, opacity: number) => Promise<void>;
  reorderLayers: (layerIds: string[]) => Promise<void>;
  /**
   * Register a drawn AFF track as an ephemeral registry layer (#802), so it
   * gets the standard visibility / opacity / z-order controls. Ephemeral means
   * session-only: it is never captured into a preset or a share link.
   */
  registerTrackLayer: (config: LayerConfig) => Promise<void>;
  unregisterTrackLayer: (layerId: string) => Promise<void>;
  setDrawerHeight: (height: DrawerHeight) => void;
  setActiveTab: (tab: 'active' | 'browse' | 'search') => void;
  setSearchTerm: (term: string) => void;
  getLayerInstance: (layerId: string) => BaseLayer | undefined;
  getEnabledLayers: () => Layer[];

  // Preset management
  loadPreset: (preset: LayerPreset) => Promise<void>;

  // Batch selection
  selectionMode: boolean;
  selectedLayerIds: Set<string>;
  toggleSelectionMode: () => void;
  toggleLayerSelection: (layerId: string) => void;
  selectAllLayers: () => void;
  deselectAllLayers: () => void;
  enableSelectedLayers: () => Promise<void>;
  disableSelectedLayers: () => Promise<void>;
}

/**
 * LayerDrawerContext
 */
export const LayerDrawerContext = createContext<LayerDrawerContextType | undefined>(undefined);

/**
 * useLayerDrawer Hook
 *
 * Access Layer Drawer context
 */
export const useLayerDrawer = (): LayerDrawerContextType => {
  const context = useContext(LayerDrawerContext);

  if (!context) {
    throw new Error('useLayerDrawer must be used within LayerDrawerProvider');
  }

  return context;
};
